/**
 * B2 — Live-status card for the Settings → Org Brain page.
 *
 * Renders enrollment, node/edge counts, last indexed timestamp, most
 * recent drift severity, last query, and Kafka consumer health. Polls
 * the BFF `/stats` endpoint every 30s (backend caches 15s so the
 * admin-QPS stays cheap).
 *
 * Shown above the enable/disable toggle when Org Brain is enabled for
 * the tenant. Hidden entirely otherwise — the operator doesn't need a
 * "no data" card for a feature they haven't opted into.
 */

import { Card, Spinner } from '@centraoneplus/ui';
import { useOrgBrainStats } from './hooks';

interface Props {
  tenantId: string;
}

const DRIFT_SEVERITY_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  info: {
    bg: 'bg-gray-100',
    fg: 'text-gray-800',
    label: 'Info',
  },
  warning: {
    bg: 'bg-amber-100',
    fg: 'text-amber-900',
    label: 'Warning',
  },
  breaking: {
    bg: 'bg-red-100',
    fg: 'text-red-900',
    label: 'Breaking',
  },
};

export function OrgBrainStatus({ tenantId }: Props) {
  const { data, isLoading, isError } = useOrgBrainStats(tenantId);

  if (isLoading) {
    return (
      <Card className="p-4" aria-busy="true" aria-live="polite">
        <Spinner />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card className="p-4">
        <p className="text-sm text-red-700" role="alert">
          Couldn&apos;t load Org Brain status. Try refreshing in a moment.
        </p>
      </Card>
    );
  }
  if (!data.enrolled) {
    return null;
  }

  const sev = data.last_drift_severity
    ? DRIFT_SEVERITY_STYLE[data.last_drift_severity]
    : null;

  return (
    <Card className="p-4 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Status</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Live read, refreshed every 30 seconds.
          </p>
        </div>
        <ConsumerHealthPill healthy={data.kafka_consumer_healthy} />
      </header>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Metric label="Nodes" value={data.node_count.toLocaleString()} />
        <Metric label="Edges" value={data.edge_count.toLocaleString()} />
        <Metric
          label="Last indexed"
          value={<RelativeTime iso={data.last_indexed_at} fallback="—" />}
        />
        <Metric
          label="Last query"
          value={<RelativeTime iso={data.last_query_at} fallback="No queries yet" />}
        />
        <Metric
          label="Queries in last 24h"
          value={data.queries_last_24h.toLocaleString()}
        />
        <Metric
          label="Last drift scan"
          value={
            data.last_drift_scan_at ? (
              <span className="inline-flex items-center gap-2">
                <RelativeTime iso={data.last_drift_scan_at} fallback="—" />
                {sev && (
                  <span
                    className={`px-1.5 py-0.5 rounded text-xs font-medium ${sev.bg} ${sev.fg}`}
                  >
                    {sev.label} ({data.drift_findings_count})
                  </span>
                )}
              </span>
            ) : (
              <span className="text-gray-500">No scan yet</span>
            )
          }
        />
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function Metric({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900">{value}</dd>
    </div>
  );
}

function ConsumerHealthPill({ healthy }: { healthy: boolean | null }) {
  if (healthy === null) {
    return (
      <span
        className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700"
        title="Kafka consumer not configured in this environment"
      >
        Consumer: not configured
      </span>
    );
  }
  if (healthy) {
    return (
      <span
        className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800"
        title="No Kafka errors in the last 5 minutes"
      >
        Consumer: healthy
      </span>
    );
  }
  return (
    <span
      className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-900"
      role="status"
      title="Consumer reported an error recently; events may be lagging"
    >
      Consumer: unhealthy
    </span>
  );
}

function RelativeTime({
  iso,
  fallback,
}: {
  iso: string | null;
  fallback: string;
}) {
  if (!iso) return <span className="text-gray-500">{fallback}</span>;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) {
    return <span className="text-gray-500">{fallback}</span>;
  }
  const deltaMs = Date.now() - when.getTime();
  const deltaSec = Math.max(1, Math.round(deltaMs / 1000));
  const label = humanizeDelta(deltaSec);
  // Full ISO goes in the title for operator debugging; the human string
  // is what the sighted user reads. `<time>` gives SR users the
  // machine-parseable value.
  return (
    <time dateTime={iso} title={iso}>
      {label} ago
    </time>
  );
}

function humanizeDelta(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}
